import React, { useState } from 'react';
import { apiFetch, apiFetchOk } from '../utils/api';

/**
 * Try to parse email-channel JSON from suggestion text.
 * Returns { subject, preview, body, cta } or null.
 */
function parseEmailResult(text) {
  if (!text || typeof text !== 'string' || text[0] !== '{') return null;
  try {
    const obj = JSON.parse(text);
    if (obj && typeof obj.subject_line === 'string' && typeof obj.body_copy === 'string') {
      return {
        subject: obj.subject_line,
        preview: obj.preview_text || '',
        body: obj.body_copy,
        cta: obj.cta_button_text || '',
      };
    }
  } catch { /* not JSON */ }
  return null;
}

/**
 * Try to parse video-script JSON from suggestion text.
 * Handles nested { video_script: { Hook: { duration, voiceover, visual }, ... } }
 * and flat { Hook: { duration, voiceover, visual }, ... }.
 * Returns { hook, problem, solution, cta } where each is { duration, voiceover, visual } or null.
 */
function parseVideoResult(text) {
  if (!text || typeof text !== 'string' || text[0] !== '{') return null;
  try {
    const obj = JSON.parse(text);
    const vs = obj.video_script || obj;
    const hook = vs.Hook;
    const solution = vs.Solution;
    if (!hook || !solution) return null;
    const normalize = (sec) => {
      if (typeof sec === 'string') return { duration: '', voiceover: sec, visual: '' };
      if (sec && typeof sec === 'object') return {
        duration: sec.duration || '',
        voiceover: sec.voiceover || '',
        visual: sec.visual || '',
      };
      return null;
    };
    const h = normalize(hook);
    const s = normalize(solution);
    if (!h || !s) return null;
    return {
      hook: h,
      problem: vs.Problem ? normalize(vs.Problem) : null,
      solution: s,
      cta: vs.CTA ? normalize(vs.CTA) : null,
    };
  } catch { /* not JSON */ }
  return null;
}

/**
 * Try to parse ad-copy JSON from suggestion text.
 * Returns { headline, body, cta } or null.
 */
function parseAdCopyResult(text) {
  if (!text || typeof text !== 'string' || text[0] !== '{') return null;
  try {
    const obj = JSON.parse(text);
    if (obj && typeof obj.headline === 'string' && typeof obj.body === 'string') {
      return { headline: obj.headline, body: obj.body, cta: obj.cta || '' };
    }
  } catch { /* not JSON */ }
  return null;
}

/**
 * Format an email result object as a readable multi-line string for clipboard.
 */
function formatEmailText(email) {
  let out = `Subject: ${email.subject}`;
  if (email.preview) out += `\nPreview: ${email.preview}`;
  out += `\n\n${email.body}`;
  if (email.cta) out += `\n\nCTA: ${email.cta}`;
  return out;
}

/**
 * Format a video result as a readable multi-line string for clipboard.
 */
function formatVideoText(v) {
  const fmt = (label, sec) => {
    if (!sec) return '';
    let out = `${label}${sec.duration ? ` (${sec.duration})` : ''}:\n${sec.voiceover}`;
    if (sec.visual) out += `\n${sec.visual}`;
    return out;
  };
  return [
    fmt('Hook', v.hook),
    fmt('Problem', v.problem),
    fmt('Solution', v.solution),
    fmt('CTA', v.cta),
  ].filter(Boolean).join('\n\n');
}

/**
 * Format an ad copy result as a readable multi-line string for clipboard.
 */
function formatAdCopyText(ad) {
  let out = `${ad.headline}\n\n${ad.body}`;
  if (ad.cta) out += `\n\n${ad.cta}`;
  return out;
}

/**
 * Get the copyable text for a suggestion — formatted if structured, raw otherwise.
 */
function getCopyText(suggestion) {
  const email = parseEmailResult(suggestion.text);
  if (email) return formatEmailText(email);
  const video = parseVideoResult(suggestion.text);
  if (video) return formatVideoText(video);
  const ad = parseAdCopyResult(suggestion.text);
  if (ad) return formatAdCopyText(ad);
  return suggestion.text || '';
}

/**
 * EmailResultCard — renders parsed email fields in a structured layout.
 */
function EmailResultCard({ subject, preview, body, cta }) {
  return (
    <div className="mai-email-card">
      <div className="mai-email-section">
        <span className="mai-email-label">SUBJECT</span>
        <div className="mai-email-subject">{subject}</div>
      </div>
      {preview && (
        <div className="mai-email-section">
          <span className="mai-email-label">PREVIEW</span>
          <div className="mai-email-preview">{preview}</div>
        </div>
      )}
      <div className="mai-email-section">
        <span className="mai-email-label">BODY</span>
        <div className="mai-email-body">{body}</div>
      </div>
      {cta && (
        <div className="mai-email-section">
          <span className="mai-email-label">CALL TO ACTION</span>
          <span className="mai-email-cta">{cta}</span>
        </div>
      )}
    </div>
  );
}

/**
 * VideoScriptCard — renders parsed video script fields in a structured layout.
 * Each section has { duration, voiceover, visual }.
 */
function VideoScriptCard({ hook, problem, solution, cta }) {
  const Section = ({ label, data }) => {
    if (!data) return null;
    return (
      <div className="mai-video-section">
        <span className="mai-video-label">
          {label}{data.duration ? ` \u00B7 ${data.duration}` : ''}
        </span>
        <div className="mai-video-text">{data.voiceover}</div>
        {data.visual && (
          <div className="mai-video-visual">{data.visual}</div>
        )}
      </div>
    );
  };
  return (
    <div className="mai-video-card">
      <Section label="HOOK" data={hook} />
      <Section label="PROBLEM" data={problem} />
      <Section label="SOLUTION" data={solution} />
      <Section label="CALL TO ACTION" data={cta} />
    </div>
  );
}

/**
 * AdCopyCard — renders parsed ad copy fields in a structured layout.
 */
function AdCopyCard({ headline, body, cta }) {
  return (
    <div className="mai-adcopy-card">
      <div className="mai-adcopy-section">
        <span className="mai-adcopy-label">HEADLINE</span>
        <div className="mai-adcopy-headline">{headline}</div>
      </div>
      <div className="mai-adcopy-section">
        <span className="mai-adcopy-label">BODY</span>
        <div className="mai-adcopy-body">{body}</div>
      </div>
      {cta && (
        <div className="mai-adcopy-section">
          <span className="mai-adcopy-label">CALL TO ACTION</span>
          <span className="mai-adcopy-cta">{cta}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Classification label and CSS class for the compliance detail badge.
 */
const CLASSIFICATION_DISPLAY = {
  COSMETIC: { label: 'Cosmetic', cls: 'cosmetic' },
  OTC_DRUG: { label: 'OTC Drug', cls: 'otc-drug' },
  DRUG: { label: 'Drug', cls: 'drug' },
  REVIEW_REQUIRED: { label: 'Review Required', cls: 'review' },
};

/**
 * MarketingSuggestions — renders generated marketing suggestions as cards
 * with compliance badges, copy, favorite, and detail expansion.
 */
export default function MarketingSuggestions({ suggestions, onFavoriteChange }) {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const filtered = filter === 'all'
    ? suggestions
    : suggestions.filter(s => s.compliance_status === filter);

  const copySuggestion = async (suggestion, idx) => {
    try {
      await navigator.clipboard.writeText(getCopyText(suggestion));
      setCopiedId(idx);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard not available */ }
  };

  const copyAllSafe = async () => {
    const safeTexts = suggestions
      .filter(s => s.compliance_status === 'safe')
      .map(s => getCopyText(s))
      .join('\n\n---\n\n');
    if (safeTexts) {
      try { await navigator.clipboard.writeText(safeTexts); } catch {}
    }
  };

  const exportCSV = () => {
    const rows = [['Text', 'Subject', 'Preview', 'Body', 'CTA', 'Hook', 'Problem', 'Solution', 'Video_CTA', 'Headline', 'Ad_Body', 'Ad_CTA', 'Status', 'Channel', 'Confidence']];
    suggestions.forEach(s => {
      const email = parseEmailResult(s.text);
      const video = parseVideoResult(s.text);
      const ad = parseAdCopyResult(s.text);
      const esc = v => `"${(v || '').replace(/"/g, '""')}"`;
      const fmtVideoCol = (sec) => sec ? esc(sec.voiceover + (sec.visual ? ' ' + sec.visual : '')) : '';
      rows.push([
        (!email && !video && !ad) ? esc(s.text) : '',
        email ? esc(email.subject) : '',
        email ? esc(email.preview || '') : '',
        email ? esc(email.body) : '',
        email ? esc(email.cta || '') : '',
        video ? fmtVideoCol(video.hook) : '',
        video ? fmtVideoCol(video.problem) : '',
        video ? fmtVideoCol(video.solution) : '',
        video ? fmtVideoCol(video.cta) : '',
        ad ? esc(ad.headline) : '',
        ad ? esc(ad.body) : '',
        ad ? esc(ad.cta || '') : '',
        s.compliance_status,
        s.channel_type,
        (s.confidence || 0).toFixed(2),
      ]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'marketing-suggestions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleFavorite = async (suggestion) => {
    try {
      if (suggestion.is_favorited) {
        await apiFetchOk(`/api/v1/marketing/favorites/${suggestion.id}`, { method: 'DELETE' });
      } else {
        await apiFetchOk('/api/v1/marketing/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggestion_id: suggestion.id }),
        });
      }
      if (onFavoriteChange) onFavoriteChange();
    } catch (err) {
      console.error('Favorite toggle failed:', err);
    }
  };

  const statusLabel = { safe: '✅ Safe', caution: '⚠️ Caution', rejected: '❌ Rejected' };
  const channelLabel = {
    product_label: 'Label', social_media: 'Social', ad_copy: 'Ad',
    video_script: 'Video', website: 'Web', email: 'Email',
  };

  const safeCount = suggestions.filter(s => s.compliance_status === 'safe').length;
  const cautionCount = suggestions.filter(s => s.compliance_status === 'caution').length;
  const rejectedCount = suggestions.filter(s => s.compliance_status === 'rejected').length;

  return (
    <div>
      {/* Stats */}
      <div className="mai-results-header">
        <h2>Results ({suggestions.length})</h2>
        <div className="mai-stats">
          <span className="mai-stat safe">{safeCount} safe</span>
          <span className="mai-stat caution">{cautionCount} caution</span>
          <span className="mai-stat rejected">{rejectedCount} rejected</span>
        </div>
      </div>

      {/* Filter + bulk actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div className="mai-filter-bar">
          {['all', 'safe', 'caution', 'rejected'].map(f => (
            <button
              key={f}
              className={`mai-filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="mai-bulk-actions">
          <button className="mai-bulk-btn" onClick={copyAllSafe}>Copy All Safe</button>
          <button className="mai-bulk-btn" onClick={exportCSV}>Export CSV</button>
        </div>
      </div>

      {/* Cards */}
      <div className="mai-cards">
        {filtered.map((s, idx) => (
          <div key={s.id || idx} className="mai-card">
            <div className="mai-card-top">
              <div className="mai-card-text">
                {(() => {
                  const emailData = parseEmailResult(s.text);
                  if (emailData) return <EmailResultCard {...emailData} />;
                  const videoData = parseVideoResult(s.text);
                  if (videoData) return <VideoScriptCard {...videoData} />;
                  const adData = parseAdCopyResult(s.text);
                  if (adData) return <AdCopyCard {...adData} />;
                  return s.text;
                })()}
              </div>
              <div className="mai-card-actions">
                <button
                  className="mai-card-btn"
                  onClick={() => copySuggestion(s, idx)}
                  title="Copy"
                >
                  {copiedId === idx ? '✓' : '📋'}
                </button>
                <button
                  className={`mai-card-btn ${s.is_favorited ? 'favorited' : ''}`}
                  onClick={() => toggleFavorite(s)}
                  title={s.is_favorited ? 'Remove bookmark' : 'Bookmark'}
                >
                  {s.is_favorited ? '★' : '☆'}
                </button>
              </div>
            </div>
            <div className="mai-card-meta">
              <span className={`mai-badge ${s.compliance_status}`}>
                {statusLabel[s.compliance_status] || s.compliance_status}
              </span>
              <span className="mai-badge-channel">
                {channelLabel[s.channel_type] || s.channel_type}
              </span>
              {s.confidence > 0 && (
                <span className="mai-badge-channel">
                  {(s.confidence * 100).toFixed(0)}% conf
                </span>
              )}
              {s.retries > 0 && (
                <span className="mai-badge-channel">
                  {s.retries} {s.retries === 1 ? 'retry' : 'retries'}
                </span>
              )}
            </div>

            {/* Expandable compliance detail */}
            {(s.rule_hits?.length > 0 || s.details || s.classification) && (
              <>
                <button
                  className="mai-detail-toggle"
                  onClick={() => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }))}
                >
                  {expanded[idx] ? '▾ Hide details' : '▸ Show compliance details'}
                </button>
                {expanded[idx] && (
                  <div className="mai-detail-panel">
                    {s.classification && (
                      <div style={{ marginBottom: 10 }}>
                        <span className={`mai-class-badge ${(CLASSIFICATION_DISPLAY[s.classification] || {}).cls || ''}`}>
                          {(CLASSIFICATION_DISPLAY[s.classification] || {}).label || s.classification}
                        </span>
                      </div>
                    )}
                    {s.confidence > 0 && (
                      <div className="mai-confidence-row">
                        <span className="mai-confidence-label">Confidence</span>
                        <div className="mai-confidence-bar">
                          <div className="mai-confidence-fill" style={{ width: `${(s.confidence * 100).toFixed(0)}%` }} />
                        </div>
                        <span className="mai-confidence-pct">{(s.confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    {s.rule_hits?.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <strong>Rule hits:</strong>
                        {s.rule_hits.map((h, i) => (
                          <div key={i} className="rule-hit">
                            • {h.rule_name || h}{h.citation ? ` (${h.citation})` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {s.details?.veto_active && (
                      <div style={{ color: '#c62828', marginTop: 4 }}>⚠ Veto rule active</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="mai-empty">No suggestions match the selected filter.</div>
        )}
      </div>
    </div>
  );
}
